import React, {useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {Tooltip, TooltipPlacement} from '@vio/atlas/Tooltip'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {
  Action,
  Category,
  Entity,
  TrackEventPayload
} from '@vio/core/src/analytics/types/Events'
import {DateValidationReason} from '@vio/core/src/searchParams/dateValidationReason'

import {setDateConfirmationTooltipShown} from '../cookies/utils'
import {getTooltipMessage} from './getTooltipMessage'

export const DateTooltipWrapper = styled.div`
  position: relative;
`

export const DateTooltipAnchor = styled.div`
  position: absolute;
  bottom: 0;
`

const DateTooltip = styled(Tooltip)`
  max-width: 311px;
  ${linariaMq.desktopXs} {
    max-width: 448px;
  }
`

export interface SearchDateTooltipWrapperProps {
  /** Whether the tooltip should be shown */
  isDateConfirmationTooltipShown?: boolean
  /** The date validation reason for scenario-specific messages */
  dateValidationReason?: DateValidationReason | null
  /** Track event function */
  trackEvent: (payload: TrackEventPayload) => void
  /** Children to wrap */
  children: React.ReactNode
}

/**
 * Presentational component that wraps children with a date confirmation tooltip.
 * Renders children in case other brands want different copy in the tooltip
 * Handles tooltip display logic, cookie management, and analytics tracking.
 */
export const SearchDateTooltipWrapper: React.FC<
  SearchDateTooltipWrapperProps
> = ({
  children,
  isDateConfirmationTooltipShown = false,
  dateValidationReason,
  trackEvent
}) => {
  const intl = useIntl()
  const tooltipMessage = getTooltipMessage(dateValidationReason)
  const [isInteracted, setIsInteracted] = useState(false)
  const [anchorMounted, setAnchorMounted] = useState(false)
  const cookieSetRef = useRef(false)
  const anchorRef = useRef<HTMLDivElement | null>(null)

  const closeTooltip = () => setIsInteracted(true)

  // Callback ref to detect when anchor element is mounted
  const setAnchorRef = (node: HTMLDivElement | null) => {
    anchorRef.current = node
    setAnchorMounted(Boolean(node))
  }

  // Only set cookie and track event when tooltip is actually displayed (anchor is mounted)
  // Use a ref to ensure we only set it once, preventing race conditions
  useEffect(() => {
    if (
      isDateConfirmationTooltipShown &&
      !isInteracted &&
      anchorMounted &&
      !cookieSetRef.current
    ) {
      // Delay cookie setting slightly to ensure tooltip is visible
      const timeoutId = setTimeout(() => {
        trackEvent({
          category: Category.System,
          entity: Entity.SearchDateToolTip,
          action: Action.Displayed
        })
        setDateConfirmationTooltipShown()
        cookieSetRef.current = true
      }, 100)

      return () => clearTimeout(timeoutId)
    }
  }, [isDateConfirmationTooltipShown, isInteracted, anchorMounted, trackEvent])

  // Block event propagation to avoid applying filters when user presses on the tooltip
  const handleOnClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation()
    e.preventDefault()
  }

  const shouldShowTooltip = isDateConfirmationTooltipShown && !isInteracted
  if (!shouldShowTooltip) return <>{children}</>

  return (
    <>
      <DateTooltip
        isOpen={Boolean(anchorRef.current)}
        anchor={anchorRef.current}
        placement={TooltipPlacement.BottomStart}
        onClose={closeTooltip}
        isInline={false}
        content={
          <Box
            textAlign="start"
            minWidth="271px"
            data-id="searchDateTooltipMessage"
            onClick={handleOnClick}
          >
            <Text variant="bodyS">{intl.formatMessage(tooltipMessage)}</Text>
          </Box>
        }
      >
        {/* Empty element has to be there otherwise there gonna be some issues with the content that being rendered by Tooltip */}
        <div></div>
      </DateTooltip>
      <DateTooltipWrapper onMouseEnter={closeTooltip}>
        {children}
        <DateTooltipAnchor ref={setAnchorRef} />
      </DateTooltipWrapper>
    </>
  )
}
